// "Give the venue a heads-up?" flow — module-scoped bus, guest active-pass
// gate, en-route charges (2 per hour), and the actual Maps launcher.
// Extracted from GuestMapSurface.js per FRONTEND_ARCHITECTURE.md.
//
// The intent bus is used instead of React Context because "Get directions"
// buttons can be rendered inside a Leaflet popup portal that doesn't
// reliably inherit React context.

const directionsIntentListeners = new Set();

export function emitDirectionsIntent(venue) {
  if (!venue) return;
  directionsIntentListeners.forEach((fn) => {
    try { fn(venue); } catch { /* one bad listener never blocks the rest */ }
  });
}

export function subscribeDirectionsIntent(fn) {
  directionsIntentListeners.add(fn);
  return () => { directionsIntentListeners.delete(fn); };
}

// `_hasActivePass` gates the "Get directions" and "Message venue" buttons.
// Defaults to TRUE so first paint never accidentally disables buttons before
// the page has finished fetching guest passes.
let _hasActivePass = true;
// `_chargesState` tracks the global 2-per-hour ping budget. When
// `chargesRemaining === 0` AND `nextChargeAt` is in the future, the third
// "Get directions" tap skips the intent modal entirely and just opens Maps.
let _chargesState = {
  chargesRemaining: null,     // null = unknown → allow modal (server enforces)
  nextChargeAt: null,         // ISO timestamp | null
};

export function setGuestActivePassStatus(hasActive) {
  _hasActivePass = !!hasActive;
}

export function setEnRouteChargesState(state) {
  if (!state) return;
  if (typeof state.charges_remaining === 'number') {
    _chargesState.chargesRemaining = state.charges_remaining;
  }
  _chargesState.nextChargeAt = state.next_charge_at || null;
}

function isGlobalLimitHit() {
  const { chargesRemaining, nextChargeAt } = _chargesState;
  if (chargesRemaining === null) return false;
  if (chargesRemaining > 0) return false;
  if (nextChargeAt) {
    const t = Date.parse(nextChargeAt);
    if (Number.isFinite(t) && t <= Date.now()) {
      _chargesState = { chargesRemaining: null, nextChargeAt: null };
      return false;
    }
  }
  return true;
}

/**
 * Fires the "guest is heading over" push. Resolves to a neutral shape on
 * network failure so callers never break because of a best-effort push.
 */
export async function notifyVenueEnRoute(venueId) {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
    if (!backendUrl || !token || !venueId) return { throttled: false, globalLimitHit: false };
    const res = await fetch(`${backendUrl}/api/venues/${encodeURIComponent(venueId)}/en-route`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ lat: null, lng: null }),
    });
    if (!res.ok) return { throttled: false, globalLimitHit: false };
    const data = await res.json().catch(() => ({}));
    setEnRouteChargesState(data);
    return {
      throttled: data?.throttled === true,
      globalLimitHit: data?.global_limit_hit === true,
    };
  } catch {
    return { throttled: false, globalLimitHit: false };
  }
}

/** Actually opens Google/Apple Maps for the venue. */
export function openDirectionsMaps(venue) {
  const lat = venue?.latitude;
  const lng = venue?.longitude;
  const name = venue?.name || '';
  const address = venue?.address || '';
  const placeId = venue?.google_place_id || '';
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const encodedName = encodeURIComponent(name);
  if (isIOS) {
    const dest = name
      ? encodeURIComponent(address ? `${name}, ${address}` : name)
      : `${lat},${lng}`;
    window.open(`maps://maps.apple.com/?daddr=${dest}`, '_blank');
    return;
  }
  if (placeId) {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodedName}&destination_place_id=${encodeURIComponent(placeId)}`,
      '_blank',
    );
    return;
  }
  const fallbackDest = name
    ? encodeURIComponent(address ? `${name}, ${address}` : name)
    : `${lat},${lng}`;
  window.open(`https://www.google.com/maps/dir/?api=1&destination=${fallbackDest}`, '_blank');
}

/**
 * Opens the venue's location on the map WITHOUT routing directions.
 * Used by "See Location Only" — guest sees the pin and can decide for
 * themselves whether to tap "Directions" inside Maps.
 */
export function openLocationOnlyMaps(venue) {
  const lat = venue?.latitude;
  const lng = venue?.longitude;
  const name = venue?.name || '';
  const address = venue?.address || '';
  const placeId = venue?.google_place_id || '';
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const encodedName = encodeURIComponent(name);
  if (isIOS) {
    // Apple Maps: `q` drops a pin, `ll` centers without routing.
    if (lat != null && lng != null) {
      window.open(`maps://maps.apple.com/?q=${encodedName || `${lat},${lng}`}&ll=${lat},${lng}`, '_blank');
      return;
    }
    const q = encodeURIComponent(address ? `${name}, ${address}` : name);
    window.open(`maps://maps.apple.com/?q=${q}`, '_blank');
    return;
  }
  if (placeId) {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodedName || `${lat},${lng}`}&query_place_id=${encodeURIComponent(placeId)}`,
      '_blank',
    );
    return;
  }
  if (lat != null && lng != null) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
    return;
  }
  const q = encodeURIComponent(address ? `${name}, ${address}` : name);
  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
}

/** Entry point every "Get directions" button calls. */
export function openDirections(venue) {
  // Guests without an active QR pass skip the "notify venue" modal and
  // go STRAIGHT to Maps directions — they can still get there, the venue
  // just doesn't get a heads-up ping.
  if (!_hasActivePass) {
    openDirectionsMaps(venue);
    return;
  }
  if (isGlobalLimitHit()) {
    openDirectionsMaps(venue);
    return;
  }
  emitDirectionsIntent(venue);
}

/** Dev-only trigger — used by `/dev/directions-intent` preview page. */
export function __triggerDirectionsIntent(venue) {
  emitDirectionsIntent(venue);
}
